import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';

import { Geraetelog } from './geraetelog';
import { desktopBruecke, istGeraet, type DesktopDateien } from './gleis';
import { ausBase64, base64 } from './webcrypto';
import { crc32 } from './zipSchreiber';

/**
 * Die Dateien der App — **eine** Tür, drei Gleise dahinter.
 *
 * Bis heute rief jedes Modul `@capacitor/filesystem` unmittelbar: 29 Stellen in zwölf
 * Dateien, jede mit `directory: Directory.Documents` daneben. Das ging, solange es zwei
 * Gleise gab: auf dem iPad ein echtes Dateisystem, im Browser IndexedDB, beides von
 * Capacitor bedient. Für den Desktop ([[drittes-gleis]]) gibt es keine Electron-Fassung des
 * Plugins — dort liefe wieder IndexedDB, und die Belege einer Prüfung lägen in einer
 * Browserablage statt in einem Ordner, den ein Mensch öffnen kann.
 *
 * ⭐ **Eine Wurzel, keine `directory`-Angabe.** Alle 29 Aufrufe zeigten auf `Documents`; die
 * Wahl war nie eine. Was hier `pfad` heißt, ist immer relativ zu dieser einen Wurzel — auf
 * dem iPad das Dokumentenverzeichnis der App, im Browser IndexedDB, am Desktop (Etappe E3b)
 * ein Ordner neben den Nutzerdaten.
 *
 * ⭐ **Text oder Base64 steht im Namen.** Capacitor entscheidet das über ein mitgegebenes
 * `encoding`, und ein vergessenes `encoding` schreibt stillschweigend Base64 in eine
 * Textdatei. Hier gibt es dafür zwei Türen, und keine davon lässt sich verwechseln.
 *
 * ⭐ **Seit E3b hat der Desktop ein echtes Dateisystem.** Ist die Brücke da, geht jede Tür
 * über sie an `node:fs/promises` im Hauptprozess (`desktop/dateien.cts`), und die Wurzel ist
 * ein sichtbarer Ordner unter `~/Documents/fairexa Stage`. Ohne Brücke bleibt alles wie
 * gehabt bei Capacitor. S. `docs/plan-desktop-und-browser.md`.
 */

/**
 * Die Türen des Desktops — oder `null`, wenn wir nicht dort laufen.
 *
 * ⚠️ **Bei jedem Aufruf gefragt, nicht einmal gemerkt.** Ein Modul, das sich das Gleis beim
 * Laden merkt, ist im Test nicht mehr umzustellen — und schlimmer: die Reihenfolge der
 * Importe entschiede darüber, ob die Brücke schon dastand.
 */
function desktopTueren(): DesktopDateien | null {
  if (istGeraet()) return null;
  return desktopBruecke()?.dateien ?? null;
}

/**
 * „Die Datei gibt es nicht" — als Fehler, nicht als Textvergleich.
 *
 * ⚠️ **Dieser eine Fall muss unterscheidbar bleiben.** Ein Löschen, das ins Leere greift, ist
 * für den Aufrufer dasselbe wie ein erfolgreiches (`storage.ts::deleteFile`); Schreibschutz,
 * volle Platte und E/A-Fehler sind es nicht. Verschwände die Unterscheidung, käme eine
 * „gelöschte" Datei beim nächsten Lesen zurück.
 *
 * ⭐ **Erkannt wird am Meldungstext, und das bleibt richtig.** `@capacitor/filesystem` hat
 * keinen über beide Plattformen einheitlichen Code:
 * - iOS (`FilesystemPlugin/FilesystemError.swift`, Fall `.fileNotFound`) liefert
 *   `OS-PLUG-FILE-0008` mit der Meldung „… does not exist.".
 * - Web (`@capacitor/filesystem/dist/esm/web.js`, IndexedDB für Browser und Vitest) wirft
 *   schlicht `Error('File does not exist.')`, ganz ohne Code.
 * Beide teilen den Text „does not exist". Am Desktop kommt `ENOENT` von `node:fs` dazu
 * (E3b) — auch das wird hier übersetzt, damit die Aufrufer **einen** Fall kennen statt drei.
 */
export class DateiFehlt extends Error {
  /**
   * Der Fehler, der darunter lag — Capacitors Meldung oder Nodes `ENOENT`.
   *
   * ⚠️ **Nicht `cause`.** Das Ziel ist ES2020 (`tsconfig.app.json`, wegen iOS 15); dort kennt
   * `Error` diese Eigenschaft noch nicht.
   */
  readonly ursache: unknown;

  /**
   * ⚠️ **Die Meldung darunter steht mit in der eigenen.** Sonst verlöre das Dateilog genau
   * die Zeile, um derentwillen es am 2026-09-21 eingeführt wurde: der Editor blieb leer, und
   * `OS-PLUG-FILE-0006` war nirgends zu sehen. Ein übersetzter Fehler darf benennen, was er
   * ist — aber nicht vergessen, woher er kam.
   */
  constructor(pfad: string, ursache: unknown) {
    const darunter = ursache instanceof Error ? ursache.message : String(ursache);
    super('Datei oder Ordner gibt es nicht: ' + pfad + ' — ' + darunter);
    this.name = 'DateiFehlt';
    this.ursache = ursache;
  }
}

/** Sieht dieser Fehler nach „gibt es nicht" aus? S. `DateiFehlt`. */
function meintFehlt(fehler: unknown): boolean {
  const nachricht = fehler instanceof Error ? fehler.message : String(fehler);
  const code = (fehler as { code?: unknown } | null)?.code;
  return nachricht.includes('does not exist') || nachricht.includes('ENOENT') || code === 'ENOENT';
}

/**
 * Führt einen Aufruf aus und übersetzt sein „gibt es nicht" in `DateiFehlt`.
 *
 * ⚠️ **Alles andere fliegt unverändert weiter.** Die Fassade verschluckt keine Fehler; sie
 * benennt genau einen.
 */
async function uebersetze<T>(pfad: string, aufruf: () => Promise<T>): Promise<T> {
  try {
    return await aufruf();
  } catch (fehler) {
    if (meintFehlt(fehler)) throw new DateiFehlt(pfad, fehler);
    throw fehler;
  }
}

/** Was beim Schreiben zusätzlich zu tun ist. */
export interface SchreibOptionen {
  /** Fehlende Ordner des Pfades mit anlegen. Capacitors `recursive`. */
  ordnerAnlegen?: boolean;
}

/** Was beim Umbenennen zusätzlich zu wissen ist. */
export interface UmbenennOptionen {
  /**
   * Die Quelle ist ein **absoluter** Pfad des Systems, keiner in unserer Wurzel.
   *
   * ⚠️ **Die Kamera liefert genau so einen** (`video.ts`): eine frische Aufnahme liegt im
   * temporären Ordner des Systems. Im Browser gibt es keine absoluten Pfade — dort liegt die
   * Aufnahme in unserer Wurzel unter `tmp/` (`videoWeb.ts::TMP_ORDNER`), und die Angabe wird
   * ignoriert. Ohne diese Unterscheidung fände das Umbenennen nichts.
   */
  vonAbsolut?: boolean;
}

/** Ein Ordner, in Dateien und Unterordner getrennt — die Frage, die alle Aufrufer stellen. */
export interface Ordnerinhalt {
  readonly dateien: string[];
  readonly ordner: string[];
}

/** Liest eine Textdatei. Wirft `DateiFehlt`, wenn es sie nicht gibt. */
export async function liesText(pfad: string): Promise<string> {
  const tueren = desktopTueren();
  if (tueren) return uebersetze(pfad, () => tueren.liesText(pfad));

  const gelesen = await uebersetze(pfad, () =>
    Filesystem.readFile({ path: pfad, directory: Directory.Documents, encoding: Encoding.UTF8 }),
  );
  return typeof gelesen.data === 'string' ? gelesen.data : '';
}

/**
 * Liest eine Datei als Base64 — Bilder, Videos, Unterschriften.
 *
 * ⚠️ **Leere Zeichenkette statt `Blob`.** Capacitor liefert im Browser je nach Fassung einen
 * `Blob`; keine Stelle der App kann damit umgehen, und ein stiller `[object Blob]` im
 * Hochladeweg wäre schlimmer als nichts.
 */
export async function liesBase64(pfad: string): Promise<string> {
  const tueren = desktopTueren();
  if (tueren) return uebersetze(pfad, () => tueren.liesBase64(pfad));

  const gelesen = await uebersetze(pfad, () =>
    Filesystem.readFile({ path: pfad, directory: Directory.Documents }),
  );
  if (typeof gelesen.data !== 'string') return '';
  // ⚠️ Im Browser legt `schreibeText` Text in IndexedDB, und `readFile` gibt ihn ohne Wandlung
  // zurück — Handschrift, Overlay und Videomarken scheiterten so an `atob` (2026-11-01).
  if (!istGeraet() && !IST_BASE64.test(gelesen.data)) {
    return base64(new TextEncoder().encode(gelesen.data));
  }
  return gelesen.data;
}

const IST_BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

/** Eine gepackte Datei fürs ZIP: rohes Deflate, dazu Prüfsumme und Grösse des Inhalts. */
export interface Gepackt {
  daten: Uint8Array;
  crc: number;
  groesse: number;
}

/**
 * Liest eine Datei und packt sie mit Deflate — für Logs im ZIP (`platform/beleguebergabe.ts`).
 *
 * Am iPad packt Swift (iOS 15 hat kein `CompressionStream`), am Desktop Node; so geht auch nur
 * das Gepackte über die Brücke. `null` heisst: hier lässt sich nicht packen — dann ungepackt.
 */
export async function liesGepackt(pfad: string): Promise<Gepackt | null> {
  if (istGeraet()) {
    const g = await uebersetze(pfad, () => Geraetelog.packe({ pfad }));
    return { daten: ausBase64(g.base64), crc: g.crc, groesse: g.groesse };
  }
  const tueren = desktopTueren();
  if (tueren) {
    const g = await uebersetze(pfad, () => tueren.liesGepackt(pfad));
    return { daten: ausBase64(g.base64), crc: g.crc, groesse: g.groesse };
  }
  if (typeof CompressionStream === 'undefined') return null;
  const roh = ausBase64(await liesBase64(pfad));
  try {
    const strom = new Blob([roh]).stream().pipeThrough(new CompressionStream('deflate-raw'));
    const daten = new Uint8Array(await new Response(strom).arrayBuffer());
    return { daten, crc: crc32(roh), groesse: roh.length };
  } catch {
    // Safari vor 16.4 kennt `deflate-raw` nicht, auch wenn es `CompressionStream` gibt.
    return null;
  }
}

/** Schreibt eine Textdatei und legt sie an, wenn es sie noch nicht gibt. */
export async function schreibeText(
  pfad: string,
  text: string,
  optionen: SchreibOptionen = {},
): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await tueren.schreibeText(pfad, text, optionen.ordnerAnlegen === true);
    return;
  }

  await Filesystem.writeFile({
    path: pfad,
    directory: Directory.Documents,
    data: text,
    encoding: Encoding.UTF8,
    ...(optionen.ordnerAnlegen ? { recursive: true } : {}),
  });
}

/** Schreibt eine Datei aus Base64 — Bilder, Videos, Medien vom Server. */
export async function schreibeBase64(
  pfad: string,
  base64: string,
  optionen: SchreibOptionen = {},
): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await tueren.schreibeBase64(pfad, base64, optionen.ordnerAnlegen === true);
    return;
  }

  await Filesystem.writeFile({
    path: pfad,
    directory: Directory.Documents,
    data: base64,
    ...(optionen.ordnerAnlegen ? { recursive: true } : {}),
  });
}

/** Hängt Text an eine Datei an und legt sie an, wenn es sie noch nicht gibt. */
export async function haengeAn(pfad: string, text: string): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await tueren.haengeAn(pfad, text);
    return;
  }

  await Filesystem.appendFile({
    path: pfad,
    directory: Directory.Documents,
    data: text,
    encoding: Encoding.UTF8,
  });
}

/** Was in einem Ordner liegt. Wirft `DateiFehlt`, wenn es den Ordner nicht gibt. */
export async function listeOrdner(pfad: string): Promise<Ordnerinhalt> {
  const tueren = desktopTueren();
  if (tueren) return uebersetze(pfad, () => tueren.listeOrdner(pfad));

  const { files } = await uebersetze(pfad, () =>
    Filesystem.readdir({ path: pfad, directory: Directory.Documents }),
  );
  return {
    dateien: files.filter((eintrag) => eintrag.type === 'file').map((eintrag) => eintrag.name),
    ordner: files.filter((eintrag) => eintrag.type === 'directory').map((eintrag) => eintrag.name),
  };
}

/** Legt einen Ordner an, samt fehlender Zwischenstufen. */
export async function legeOrdnerAn(pfad: string): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await tueren.legeOrdnerAn(pfad);
    return;
  }

  await Filesystem.mkdir({ path: pfad, directory: Directory.Documents, recursive: true });
}

/**
 * Gibt es das?
 *
 * ⭐ **Die Frage, die `stat` in der App wirklich beantwortet.** Kein Aufrufer wollte je Größe
 * oder Zeitstempel wissen — nur, ob etwas da ist (`ordner.ts::stelleOrdnerSicher`).
 */
export async function gibtEs(pfad: string): Promise<boolean> {
  const tueren = desktopTueren();
  if (tueren) return tueren.gibtEs(pfad);

  try {
    await Filesystem.stat({ path: pfad, directory: Directory.Documents });
    return true;
  } catch {
    // ⚠️ **Auch ein anderer Fehler heißt hier „nein".** Der einzige Aufrufer legt danach an
    // und meldet, wenn das misslingt; eine Unterscheidung hier brächte ihm nichts.
    return false;
  }
}

/** Verschiebt eine Datei innerhalb der Wurzel — oder aus dem System hinein, s. `vonAbsolut`. */
export async function benenneUm(
  von: string,
  nach: string,
  optionen: UmbenennOptionen = {},
): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    // ⚠️ `vonAbsolut` bleibt hier ohne Wirkung — am Desktop nimmt die Aufnahme denselben Weg
    // wie im Browser und liegt schon in unserer Wurzel unter `tmp/` (`videoWeb.ts`).
    await uebersetze(von, () => tueren.benenneUm(von, nach));
    return;
  }

  const absolut = optionen.vonAbsolut === true && istGeraet();
  await uebersetze(von, () =>
    Filesystem.rename({
      from: von,
      ...(absolut ? {} : { directory: Directory.Documents }),
      to: nach,
      toDirectory: Directory.Documents,
    }),
  );
}

/** Löscht eine Datei. Wirft `DateiFehlt`, wenn es sie nicht gibt. */
export async function loescheDatei(pfad: string): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await uebersetze(pfad, () => tueren.loescheDatei(pfad));
    return;
  }

  await uebersetze(pfad, () =>
    Filesystem.deleteFile({ path: pfad, directory: Directory.Documents }),
  );
}

/** Löscht einen Ordner samt Inhalt. */
export async function loescheOrdner(pfad: string): Promise<void> {
  const tueren = desktopTueren();
  if (tueren) {
    await uebersetze(pfad, () => tueren.loescheOrdner(pfad));
    return;
  }

  await uebersetze(pfad, () =>
    Filesystem.rmdir({ path: pfad, directory: Directory.Documents, recursive: true }),
  );
}

/**
 * Die Adresse, unter der ein `<video>` oder `<img>` diese Datei abspielen kann — oder `null`,
 * wenn das Gleis keine hat.
 *
 * ⚠️ **Im Browser gibt es keine.** IndexedDB kennt keine URL, die ein `<video>` öffnen
 * könnte; dort bleibt die Datenadresse der Rückfall, und den bildet der Aufrufer
 * (`medienAdresse.ts`).
 *
 * ⭐ **Am Desktop ist es `fairexa-datei://`** — ein eigenes Schema, bedient von
 * `desktop/main.cts::bedieneDatei` **mit Bereichslesen**. Ohne das lüde Chromium für jeden
 * Sprung im Annotationseditor die ganze Aufnahme neu. Die Adresse zu bilden kostet keinen
 * Aufruf; ob es die Datei gibt, zeigt sich beim Abspielen.
 */
export async function adresse(pfad: string): Promise<string | null> {
  const tueren = desktopTueren();
  if (tueren) return tueren.adresse(pfad);

  if (!istGeraet()) return null;
  const { uri } = await uebersetze(pfad, () =>
    Filesystem.getUri({ path: pfad, directory: Directory.Documents }),
  );
  return Capacitor.convertFileSrc(uri);
}
